import type { Repository } from 'typeorm';
import type { BillingPlan } from './billing-plan.entity.js';

export type BillingPlanSource = 'current' | 'previous' | 'future' | 'legacy';

export function resolveBillingPlan<
  T extends Pick<
    BillingPlan,
    'billingStartDate' | 'billingEndDate' | 'effectiveFrom' | 'effectiveUntil'
  >,
>(plans: T[], at = new Date()): { plan: T | null; source: BillingPlanSource } {
  const time = at.getTime();
  const start = (plan: T) => new Date(plan.effectiveFrom).getTime();
  const end = (plan: T) =>
    plan.effectiveUntil === null ? Infinity : new Date(plan.effectiveUntil).getTime();
  const periodStart = (plan: T) =>
    plan.billingStartDate === null ? start(plan) : new Date(plan.billingStartDate).getTime();
  const periodEnd = (plan: T) =>
    plan.billingEndDate === null ? Infinity : new Date(plan.billingEndDate).getTime();
  const current = plans
    .filter(
      (plan) =>
        plan.billingStartDate !== null &&
        plan.billingEndDate !== null &&
        start(plan) <= time &&
        end(plan) > time &&
        periodStart(plan) <= time &&
        periodEnd(plan) > time,
    )
    .sort((a, b) => start(b) - start(a))
    .at(0);
  if (current) return { plan: current, source: 'current' };
  const previous = plans
    .filter((plan) => start(plan) <= time && periodStart(plan) <= time)
    .sort((a, b) => periodStart(b) - periodStart(a) || start(b) - start(a))
    .at(0);
  if (previous) return { plan: previous, source: 'previous' };
  const future = [...plans]
    .sort((a, b) => periodStart(a) - periodStart(b) || start(a) - start(b))
    .at(0);
  return future ? { plan: future, source: 'future' } : { plan: null, source: 'legacy' };
}

export async function findBillingPlan(
  repository: Repository<BillingPlan>,
  organisationId: string,
  at = new Date(),
): Promise<BillingPlan | null> {
  const plans = await repository.find({ where: { organisationId } });
  return resolveBillingPlan(plans, at).plan;
}

/** Permission decisions must not inherit the rate fallback of an expired or future plan. */
export async function findEffectiveBillingPlan(
  repository: Repository<BillingPlan>,
  organisationId: string,
  at = new Date(),
): Promise<BillingPlan | null> {
  const plans = await repository.find({ where: { organisationId } });
  return (
    plans
      .filter(
        (plan) =>
          plan.effectiveFrom <= at && (plan.effectiveUntil === null || plan.effectiveUntil > at),
      )
      .sort((a, b) => b.effectiveFrom.getTime() - a.effectiveFrom.getTime())
      .at(0) ?? null
  );
}
